// src/components/auth/AuthorLogin.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, UserPlus, 
  LogIn, 
  UserCog, 
  ClipboardCheck, 
  BookOpen,  
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from "../../utils/axios";
import { ensureLeading } from '../../utils/utils.global';

const AuthorLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    pass: '',
    recaptchaToken: ''
  });

  const [errors, setErrors] = useState({});
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);

  // Clear message after timeout
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    // Load reCAPTCHA script
    const loadRecaptcha = () => {
      if (window.grecaptcha && document.getElementById('recaptcha-container')) {
        // Check if already rendered
        if (recaptchaWidgetId.current === null) {
          try {
            recaptchaWidgetId.current = window.grecaptcha.render('recaptcha-container', {
              sitekey: '6LcEcsUrAAAAACd3CAtZIO54BjvF7viwD__b0vTB',
              callback: handleRecaptcha,
              'expired-callback': handleRecaptchaExpired
            });
          } catch (error) {
            console.error('reCAPTCHA render error:', error);
          }
        }
      }
    };

    // Check if reCAPTCHA is already loaded
    if (window.grecaptcha && window.grecaptcha.render) {
      loadRecaptcha();
    } else {
      // Define the callback function
      window.onRecaptchaLoad = () => {
        loadRecaptcha();
      };

      // Load the script only if it's not already present
      const existingScript = document.querySelector('script[src*="recaptcha"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }

    // Cleanup function
    return () => {
      recaptchaWidgetId.current = null;
    };
  }, []);

 
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear message when user types
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleRecaptcha = (response) => {
    setRecaptchaVerified(!!response);
    setFormData(prev => ({
      ...prev,
      recaptchaToken: response
    }));
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaVerified(false);
    setFormData(prev => ({
      ...prev,
      recaptchaToken: ''
    }));
    setMessage({ type: 'error', text: 'reCAPTCHA verification expired. Please complete it again.' });
  };
const handleRedirect = async () =>{
   // Determine redirect based on user permissions
          const returnUrl = searchParams.get('returnUrl');
        const redirectPath = ensureLeading(returnUrl || 'dashboard', 'slash');
          // Redirect after successful login
            navigate(redirectPath);
}

//  // handle page redicet if the user is logged in on first visit 
  useEffect(() =>{

    if(isAuthenticated){
     handleRedirect()
    }
  },[isAuthenticated, handleRedirect])

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.pass) {
      newErrors.password = 'Password is required';
    } else if (formData.pass.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset reCAPTCHA safely
  const resetRecaptcha = () => {
    if (window.grecaptcha && recaptchaWidgetId.current !== null) {
      try {
        window.grecaptcha.reset(recaptchaWidgetId.current);
      } catch (error) {
        console.error('Error resetting reCAPTCHA:', error);
      }
    }
    setRecaptchaVerified(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!recaptchaVerified && process.env.NODE_ENV !== 'development') {
      setMessage({ type: 'error', text: 'Please complete the reCAPTCHA verification' });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      // Make actual API call to login endpoint with all required fields
      const response = await axios.post('/api/authors/login', {
        email: formData.email,
        pass: formData.pass,
        recaptchaToken: formData.recaptchaToken || 'development-skip'
      });

      // Handle successful login
      if (response.data.status === 'success') {
        const userData = response.data.user;
        const token = response.data.token;

        
        // FIXED: Call login with correct parameters (email, password, role, token, userData)
        const loginResult = await login(
          formData.email,      // email
          formData.pass,       // password
          'author',            // role
          token,               // token
          userData             // userData
        );

        if (loginResult.success) {
          setMessage({
            type: 'success',
            text: response.data.message || 'Login successful! Redirecting...'
          });

          // Determine redirect based on user permissions
      await handleRedirect()
        } else {
          setMessage({ type: 'error', text: loginResult.error || 'Login failed' });
          resetRecaptcha();
        }
      } else {
        // Handle error response
        setMessage({ type: 'error', text: response.data.message || 'Invalid email or password' });
        resetRecaptcha();
      }

    } catch (error) {
      console.error('Login error:', error);

      // Handle different error scenarios based on error codes from backend
      let errorMessage = 'An error occurred during login. Please try again.';

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        // Handle specific error codes from backend
        if (data.error_code === 'MISSING_FIELDS') {
          errorMessage = data.message || 'Please fill all fields';
        } else if (data.error_code === 'MISSING_RECAPTCHA') {
          errorMessage = data.message || 'Please complete reCAPTCHA verification';
        } else if (data.error_code === 'INVALID_RECAPTCHA') {
          errorMessage = data.message || 'reCAPTCHA verification failed. Please try again.';
        } else if (data.error_code === 'INVALID_CREDENTIALS') {
          errorMessage = data.message || 'Invalid email or password';
        } else if (status === 401) {
          errorMessage = data.message || 'Invalid email or password';
        } else if (status === 403) {
          errorMessage = data.message || 'Your account has been suspended. Please contact support.';
        } else if (status === 404) {
          errorMessage = data.message || 'Account not found. Please sign up first.';
        } else if (status === 429) {
          errorMessage = 'Too many login attempts. Please try again later.';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = data.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your internet connection.';
      }

      setMessage({ type: 'error', text: errorMessage });

      // Reset reCAPTCHA on error
      resetRecaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f6ecf6] min-h-screen flex flex-col">
      <div className="flex-1 flex">
        {/* Sidebar */}
        <div className="sidebar w-64 hidden lg:block" style={{ background: 'linear-gradient(to bottom, #250242, #550f4f)' }}>
          <div className="p-6">
            <h2 className="text-xl font-bold text-white mb-6">Submission Portal</h2>

            <div className="mb-6">
              <div className="sidebar-title text-purple-300 text-xs uppercase tracking-wider px-4 mb-2">Account</div>
              <Link 
                to="/portal/signup" 
                className="flex items-center px-4 py-3 text-white hover:bg-white/10 border-l-4 border-transparent hover:border-yellow-500 transition-all"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Sign Up
              </Link>
              <Link 
                to="#" 
                className="flex items-center px-4 py-3 text-white bg-white/10 border-l-4 border-yellow-500"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Link>
            </div>

            <div className="mb-6">
              <div className="sidebar-title text-purple-300 text-xs uppercase tracking-wider px-4 mb-2">Resources</div>
              <a 
                href="https://asfirj.org/authors.html" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center px-4 py-3 text-white hover:bg-white/10 border-l-4 border-transparent hover:border-yellow-500 transition-all"
              >
                <UserCog className="w-4 h-4 mr-2" />
                Author Guidelines
              </a>
              <a 
                href="https://asfirj.org/reviewers.html" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center px-4 py-3 text-white hover:bg-white/10 border-l-4 border-transparent hover:border-yellow-500 transition-all"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Reviewer Guidelines
              </a>
              <a 
                href="https://asfirj.org/issues" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center px-4 py-3 text-white hover:bg-white/10 border-l-4 border-transparent hover:border-yellow-500 transition-all"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Browse Issues
              </a>
            </div>

            <div className="mb-6">
              <div className="sidebar-title text-purple-300 text-xs uppercase tracking-wider px-4 mb-2">Support</div>
              <a 
                href="https://asfirj.org/contact.html" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center px-4 py-3 text-white hover:bg-white/10 border-l-4 border-transparent hover:border-yellow-500 transition-all"
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact Us
              </a>
            </div>
          </div>
        </div>

        {/* Login Form */}
        <div className="flex-1 p-4 md:p-8">
          <div className="max-w-2xl mx-auto">
            {/* Mobile Navigation Tabs */}
            <div className="lg:hidden mb-6">
              <div className="bg-white rounded-lg shadow-sm p-1 flex">
                <Link to="/portal/signup" className="flex-1 text-center py-2 px-4 rounded-md text-gray-600 font-medium hover:bg-[#f6ecf6]">
                  Sign Up
                </Link>
                <Link to="#" className="flex-1 text-center py-2 px-4 rounded-md" style={{ backgroundColor: '#8a1e78ff', color: 'white' }}>
                  Sign In
                </Link>
              </div>
            </div>

            {/* Login Form */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden fade-in">
              <div className="p-4 md:p-8">
                <h3 className="text-2xl font-bold text-center text-gray-800 mb-6">Sign In to Your Account</h3>

                {message.text && (
                  <div className={`mb-6 p-4 rounded-lg flex items-center ${message.type === 'error'
                      ? 'bg-red-50 border border-red-200 text-red-700'
                      : 'bg-green-50 border border-green-200 text-green-700'
                    }`}>
                    {message.type === 'error' ? (
                      <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                    )}
                    <span className="text-sm">{message.text}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="author@example.com"
                        disabled={isSubmitting}
                      />
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div>
                    <label htmlFor="pass" className="block text-sm font-medium text-gray-700 mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="pass"
                        name="pass"
                        value={formData.pass}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-10 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.password ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="••••••••"
                        disabled={isSubmitting}
                      />
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        disabled={isSubmitting}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                    )}
                  </div>

                  {/* reCAPTCHA Widget */}
                  <div className="mt-6">
                    <div id="recaptcha-container" className="flex justify-center"></div>
                    {process.env.NODE_ENV === 'development' && (
                      <p className="text-xs text-amber-600 mt-1 text-center">
                        Development mode: reCAPTCHA can be skipped
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={(!recaptchaVerified && process.env.NODE_ENV !== 'development') || isSubmitting}
                      className={`w-full py-3 px-4 rounded-md font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 ${(!recaptchaVerified && process.env.NODE_ENV !== 'development') || isSubmitting
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-purple-700'
                        }`}
                      style={{ backgroundColor: ((!recaptchaVerified && process.env.NODE_ENV !== 'development') || isSubmitting) ? '#c4b5fd' : '#8a1e78ff' }}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <Loader className="w-5 h-5 mr-2 animate-spin" />
                          Signing in...
                        </span>
                      ) : (
                        'Sign In'
                      )}
                    </button>

                    <div className="text-center mt-6 space-y-3">
                      <p className="text-gray-600">
                        Don't have an account?{' '}
                        <Link to="/portal/signup" className="font-medium hover:underline" style={{ color: '#8a1e78ff' }}>
                          Click here to create one
                        </Link>
                      </p>
                      <p className="text-gray-600">
                        Forgotten Password?{' '}
                        <Link to="/portal/forgot-password" className="font-medium hover:underline" style={{ color: '#8a1e78ff' }}>
                          Click here to retrieve
                        </Link>
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorLogin;